import { useEffect, useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import {
  CTS_REVIEW_PERIODIC_CADENCES, CTS_REVIEW_CADENCE_LABEL, type CtsReviewPeriodicCadence,
  CTS_REQUIRE_DIFFERENT_APPROVER_LABEL, CTS_REQUIRE_DIFFERENT_APPROVER_HINT,
} from '@grc/shared';
import { RequireRole } from '@/components/auth/RequireRole';
import { useAuthStore } from '@/store/auth.store';
import { useReviewSettings, useUpdateReviewSettings } from '@/api/hooks/useCtsReview';

/**
 * CAPR S3 — the organization's REVIEW DEFAULT (UX-10, FR-N6, BR-C1).
 *
 * ⚠ A DEFAULT, NEVER A FRAMEWORK REQUIREMENT. Most frameworks set no fixed review frequency; annual is
 * a convention. The page says "default" and nothing more, and a control that needs a different cadence
 * gets a per-control override with its rationale on the Control Hub.
 *
 * ⚠ THE SERVER DECIDES. Saving recalculates every control's next review date on the server; this page
 * shows the zone those dates are counted in, and says so when the stored zone could not be used.
 */
export function AssessmentReviewPage() {
  return (
    <RequireRole roles={['risk_manager', 'super_admin']} label="Assessment Review">
      <AssessmentReviewBody />
    </RequireRole>
  );
}

function errorText(err: unknown): string {
  const e = err as { response?: { data?: { errors?: { message?: string }[] } }; message?: string };
  return e?.response?.data?.errors?.[0]?.message ?? e?.message ?? 'The review default could not be saved.';
}

function AssessmentReviewBody() {
  const { entitledModules } = useAuthStore();
  const entitled = entitledModules.includes('compliance');
  const { data, isLoading, isError } = useReviewSettings();
  const update = useUpdateReviewSettings();
  const [choice, setChoice] = useState<CtsReviewPeriodicCadence | null>(null);

  useEffect(() => { if (data) setChoice(data.review_cadence); }, [data]);

  if (!entitled) {
    return (
      <p className="text-[13px] text-text-secondary" data-testid="review-settings-not-entitled">
        Assessment review settings are part of the Compliance module, which is not enabled for your organization.
      </p>
    );
  }
  if (isLoading || !choice) {
    return isError
      ? <p className="text-[13px] text-cert-expired-fg">The review settings could not be loaded.</p>
      : <Loader2 className="h-4 w-4 animate-spin text-text-secondary" aria-label="Loading" />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <CalendarClock className="h-5 w-5 text-accent-cyan" aria-hidden="true" />
        <h1 className="text-[19px] font-semibold text-text-primary">Assessment Review</h1>
      </div>

      <section className="space-y-3 rounded-[10px] border border-cert-neutral-border bg-bg-surface p-5">
        <h2 className="text-[14px] font-semibold text-text-primary">Review default</h2>
        <p className="text-[12.5px] text-text-secondary">
          How often each control&apos;s implementation status is re-assessed, unless the control has its own
          cadence. This is your organization&apos;s default, not a framework requirement.
        </p>
        <label className="block text-[12px] text-text-secondary">
          Default cadence
          <select
            value={choice}
            onChange={(e) => setChoice(e.target.value as CtsReviewPeriodicCadence)}
            data-testid="review-default-select"
            className="mt-[4px] block w-[240px] rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-[13px] text-text-primary"
          >
            {CTS_REVIEW_PERIODIC_CADENCES.map((c) => <option key={c} value={c}>{CTS_REVIEW_CADENCE_LABEL[c]}</option>)}
          </select>
        </label>
        <p className="text-[11.5px] text-text-faint" data-testid="review-default-zone">
          Next review dates are counted in your organization&apos;s time zone ({data!.time_zone}).
          {data!.time_zone_fallback && ' Your organization’s saved time zone is not recognised, so UTC is used until it is corrected in Organization settings.'}
        </p>
        {update.isError && <p className="text-[12px] text-cert-expired-fg" role="alert">{errorText(update.error)}</p>}
        {update.isSuccess && !update.isPending && (
          <p className="text-[12px] text-cert-valid-fg" role="status">
            {update.data.changed ? 'Saved. Next review dates now use this default.' : 'No change — this is already the default.'}
          </p>
        )}
        <button
          type="button"
          onClick={() => update.mutate({ review_cadence: choice })}
          disabled={update.isPending || choice === data!.review_cadence}
          data-testid="review-default-save"
          className="rounded-[7px] bg-accent-cyan px-[14px] py-[7px] text-[12.5px] font-medium text-white disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </section>

      <ApprovalSeparationSection current={data!.require_different_approver} />
    </div>
  );
}

/**
 * ⚠ CAPR S5 — SEPARATION OF DUTIES. One organization setting; the server enforces it inside the approve
 * transaction against who SUBMITTED the assessment. This section only states and saves it.
 */
function ApprovalSeparationSection({ current }: { current: boolean }) {
  const save = useUpdateReviewSettings();
  const [on, setOn] = useState(current);
  useEffect(() => { setOn(current); }, [current]);
  return (
    <section className="space-y-3 rounded-[10px] border border-cert-neutral-border bg-bg-surface p-5" data-testid="approval-separation-section">
      <h2 className="text-[14px] font-semibold text-text-primary">Approval</h2>
      <label className="flex items-start gap-[10px] text-[12.5px] text-text-primary">
        <input
          type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)}
          data-testid="require-different-approver-toggle" className="mt-[3px] h-[14px] w-[14px] accent-[#009FFD]"
        />
        <span>
          <span className="block font-medium">{CTS_REQUIRE_DIFFERENT_APPROVER_LABEL}</span>
          <span className="block text-[12px] text-text-secondary">{CTS_REQUIRE_DIFFERENT_APPROVER_HINT}</span>
        </span>
      </label>
      {save.isError && <p className="text-[12px] text-cert-expired-fg" role="alert">{errorText(save.error)}</p>}
      {save.isSuccess && !save.isPending && (
        <p className="text-[12px] text-cert-valid-fg" role="status">
          {save.data.changed ? 'Saved.' : 'No change.'}
        </p>
      )}
      <button
        type="button"
        onClick={() => save.mutate({ require_different_approver: on })}
        disabled={save.isPending || on === current}
        data-testid="require-different-approver-save"
        className="rounded-[7px] bg-accent-cyan px-[14px] py-[7px] text-[12.5px] font-medium text-white disabled:opacity-50"
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </section>
  );
}
